import { WHITEOUT_TEXT_THRESHOLD } from '@/constants/constants';
import { Z_INDEX } from '@/constants/ui';
import { coordinatesIntersection, pointsToCoordinates } from '@/helpers/pdfHelpers';
import { textItemToEditableTextBoxCoordinates } from '@/helpers/utils';
import { AnnotationActionType } from '@/reducers/annotationsReducer';
import { AppStateAction } from '@/reducers/appReducer';
import { RectangleShape, Shape, ShapeTypes } from '@/types/shapes';
import { Coordinates } from '@/types';

export const shouldStackAction = (action: AppStateAction, lastActionType: AppStateAction['type'] | null) => {
  if (lastActionType !== action.type) return true;

  const nonStackableActions = [AnnotationActionType.UPDATE];

  return !nonStackableActions.includes(action.type as any);
};

/** An area erased by the whiteout tool, with its position in the render order. */
export type WhiteoutArea = {
  /** Id of the rectangle that erased it — identifies the area's mask element in the text layer. */
  id: string;
  rect: Coordinates;
  layerNumber: number;
};

/**
 * The areas erased by the whiteout tool on a page, in canvas space. Derived from the current
 * shapes rather than stored anywhere, so moving or removing a rectangle updates everything that
 * depends on it. Plain shape-tool rectangles (isWhiteout unset) are not erase areas.
 */
export const whiteoutAreasOf = (shapes: Shape[], zIndexMap: Record<string, number>): WhiteoutArea[] =>
  shapes
    .filter((shape): shape is RectangleShape => shape.type === ShapeTypes.Rectangle && shape.isWhiteout === true)
    .map((shape) => ({
      id: shape.id,
      rect: pointsToCoordinates(shape.canvasPoints),
      layerNumber: zIndexMap[shape.id] ?? Z_INDEX.SHAPES,
    }));

/**
 * The erase areas that actually hide a text box: the ones drawn over it. An erase rectangle
 * covers what is beneath it and nothing else, so text placed on top of an erased region — a new
 * box the user adds there — is unaffected, exactly as it looks on screen.
 */
const areasAbove = (textLayerNumber: number, whiteoutAreas: WhiteoutArea[]): WhiteoutArea[] =>
  whiteoutAreas.filter((area) => area.layerNumber > textLayerNumber);

/**
 * A text box is erased once an erase area above it covers most of it: the text is hidden under
 * the fill on screen and in the exported document, so editing it would be meaningless and the
 * box is made inert. Derived from live positions rather than a stored flag, so moving either the
 * text or the rectangle out of overlap brings the box back — there is nothing to fall stale.
 *
 * Used only to drop the selection when a rectangle lands over the active box, so its frame and
 * toolbar aren't left stranded on top of the fill. Interactivity itself needs no such test: the
 * text layer renders an EraseMask over each area, and the browser's hit-testing makes the covered
 * pixels of any box unreachable while the parts sticking out stay live.
 */
export const isTextItemWhitedOut = (
  textCanvasCoordinates: Coordinates,
  textLayerNumber: number,
  whiteoutAreas: WhiteoutArea[],
): boolean =>
  areasAbove(textLayerNumber, whiteoutAreas).some(
    (area) =>
      coordinatesIntersection(textItemToEditableTextBoxCoordinates(textCanvasCoordinates), area.rect) >
      WHITEOUT_TEXT_THRESHOLD,
  );

/**
 * Whether an erase area above the text box overlaps it at all. Used to hold such a box down in
 * the render order: bringing it to the top on select would lift its erased part above the fill
 * that is supposed to be hiding it.
 */
export const isTextItemTouchingWhiteout = (
  textCanvasCoordinates: Coordinates,
  textLayerNumber: number,
  whiteoutAreas: WhiteoutArea[],
): boolean =>
  areasAbove(textLayerNumber, whiteoutAreas).some(
    (area) => coordinatesIntersection(textItemToEditableTextBoxCoordinates(textCanvasCoordinates), area.rect) > 0,
  );
